import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// FC262: guided Seller Hub SOLD research only. Sessions and eBay listing APIs are never called.
const SOURCE = "EBAY_AU_PRODUCT_RESEARCH";
const display = (value, missing = "Not recorded") => value === null || value === undefined ||
  String(value).trim() === "" ? missing : String(value);
const verifiedFinish = (value) => {
  const str = String(value || "").trim();
  return str && !["unknown", "none", "null"].includes(str.toLowerCase()) ? str : null;
};
const dateString = (value) => new Date(value).toISOString().slice(0, 10);
const initialPeriod = () => ({
  start: dateString(Date.now() - 30 * 86400000),
  end: dateString(Date.now()),
});
const priceValue = (value) => value === null || value === undefined ? "" : String(value);
const textValue = (value) => value === null || value === undefined ? "" : String(value);

function initialQuery(snapshot) {
  const name = String(snapshot?.card_name || "").trim().replace(/\s+/g, " ");
  const title = String(snapshot?.listing_title || "").trim();
  const collectorNumber = /\b\d{1,4}\s*\/\s*\d{1,4}\b/;
  const number = (name.match(collectorNumber)?.[0] || title.match(collectorNumber)?.[0] || "")
    .replace(/\s*\/\s*/g, "/");
  // Broad discovery query: do not automatically add set, finish, condition or rarity.
  // Exact comparable matching is assessed from the SOLD results, not the search text.
  if (name) return [name.replace(collectorNumber, "").trim(), number].filter(Boolean).join(" ");
  // If no structured name exists, use only the title up to its first collector number.
  // Unstructured titles without a collector number remain editable for manual correction.
  if (number) return `${title.slice(0, title.search(collectorNumber)).trim()} ${number}`.trim();
  return title;
}
function exclusionsQuery(value) {
  return String(value || "").split(",").map((part) => part.trim().replace(/^-+/, ""))
    .filter(Boolean).map((part) => `-${part}`).join(" ");
}
function buildUrl(query) {
  const end = Date.now();
  const start = end - 30 * 86400000;
  const params = new URLSearchParams({
    marketplace: "EBAY-AU", keywords: query, dayRange: "30", endDate: String(end),
    startDate: String(start), categoryId: "0", offset: "0", limit: "50",
    tabName: "SOLD", tz: "Australia/Sydney",
  });
  return `https://www.ebay.com.au/sh/research?${params}`;
}

export default function AIProductResearchDialog({ open, run, onClose, onSaved }) {
  const [baseQuery, setBaseQuery] = useState("");
  const [exclusions, setExclusions] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [appliedFilters, setAppliedFilters] = useState("");
  const [filtersVerified, setFiltersVerified] = useState(false);
  const [soldCount, setSoldCount] = useState("");
  const [average, setAverage] = useState("");
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const [confirmNoSales, setConfirmNoSales] = useState(false);
  const [priceBasis, setPriceBasis] = useState("UNKNOWN");
  const [matchQuality, setMatchQuality] = useState("UNCERTAIN");
  const [matchNotes, setMatchNotes] = useState("");
  const [sourceNotes, setSourceNotes] = useState("");
  const [status, setStatus] = useState("IN_PROGRESS");
  const [observedAt, setObservedAt] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [copyNotice, setCopyNotice] = useState("");
  const snapshot = run?.snapshot_json || {};
  const hasFinish = Boolean(verifiedFinish(snapshot.fc256_verified_finish));
  const readOnly = run?.status !== "IN_PROGRESS";
  const fullQuery = useMemo(() => [baseQuery.trim(), exclusionsQuery(exclusions)]
    .filter(Boolean).join(" ").trim(), [baseQuery, exclusions]);
  const researchUrl = useMemo(() => buildUrl(fullQuery), [fullQuery]);

  useEffect(() => {
    if (!open || !run?.id) return undefined;
    const controller = new AbortController();
    let active = true;
    const dates = initialPeriod();
    setBaseQuery(initialQuery(snapshot)); setExclusions("");
    setPeriodStart(dates.start); setPeriodEnd(dates.end);
    setAppliedFilters(""); setFiltersVerified(false); setSoldCount("");
    setAverage(""); setLow(""); setHigh(""); setConfirmNoSales(false);
    setPriceBasis("UNKNOWN"); setMatchQuality("UNCERTAIN"); setMatchNotes("");
    setSourceNotes(""); setStatus("IN_PROGRESS"); setObservedAt(new Date().toISOString());
    setHistory([]); setError(""); setNotice(""); setCopyNotice(""); setLoading(true);
    axios.get(`/api/ai-operator/research-runs/${run.id}/ebay-product-research`, { signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;
        if (data?.ok !== true || data?.research?.listing_id !== run.listing_id) {
          throw new Error("Product Research returned a different listing or invalid response");
        }
        const found = data.research.revisions || [];
        setHistory(found);
        const saved = data.research.latest?.evidence_json;
        if (!saved) return;
        const suffix = exclusionsQuery(saved.exclusion_terms);
        const completeQuery = String(saved.search_query || "");
        setBaseQuery(suffix && completeQuery.endsWith(` ${suffix}`)
          ? completeQuery.slice(0, -suffix.length).trim() : completeQuery);
        setExclusions(textValue(saved.exclusion_terms));
        setPeriodStart(saved.period_start); setPeriodEnd(saved.period_end);
        setAppliedFilters(saved.applied_filters); setFiltersVerified(saved.filters_verified);
        setSoldCount(priceValue(saved.sold_count)); setAverage(priceValue(saved.average_price));
        setLow(priceValue(saved.low_price)); setHigh(priceValue(saved.high_price));
        setConfirmNoSales(saved.confirm_no_sales); setPriceBasis(saved.price_basis);
        setMatchQuality(saved.match_quality); setMatchNotes(saved.match_notes);
        setSourceNotes(saved.source_notes); setStatus(saved.evidence_status);
        setObservedAt(saved.observed_at);
      })
      .catch((err) => {
        if (!active || axios.isCancel(err)) return;
        setError(err?.response?.data?.error || err?.message || "Could not load saved research");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [open, run?.id, run?.listing_id]); // Research snapshot is immutable for a run.

  const validate = () => {
    if (!fullQuery) return "Search query is required.";
    if (!periodStart || !periodEnd || periodStart > periodEnd) return "A valid observed research period is required.";
    if (soldCount !== "" && (!/^\d+$/.test(String(soldCount)) || Number(soldCount) > 10000000)) {
      return "Sold count must be a nonnegative whole number, or blank if unavailable.";
    }
    const amount = (value) => value === "" ? null : Number(value);
    const prices = [average, low, high];
    if (prices.some((p) => p !== "" && (!/^\d+(\.\d{1,2})?$/.test(String(p)) || !Number.isFinite(Number(p))))) {
      return "Prices must be nonnegative AUD amounts with at most two decimals.";
    }
    const count = soldCount === "" ? null : Number(soldCount);
    if (count === 0 && (!confirmNoSales || prices.some((p) => p !== ""))) {
      return "Genuine zero sales must be confirmed; leave all sold prices blank.";
    }
    if (count === null && (confirmNoSales || prices.some((p) => p !== ""))) {
      return "Missing sold count is not zero: leave prices blank and do not confirm no sales.";
    }
    if (count > 0 && average === "") return "Average sold price is required when sales were observed.";
    if (count > 0 && confirmNoSales) return "Uncheck zero-sales confirmation for positive sales.";
    if (low !== "" && average !== "" && amount(low) > amount(average)) return "Low sold price exceeds average.";
    if (high !== "" && average !== "" && amount(average) > amount(high)) return "Average sold price exceeds high.";
    if (low !== "" && high !== "" && amount(low) > amount(high)) return "Low sold price exceeds high.";
    if (!hasFinish && matchQuality === "EXACT") return "Verified finish is missing. Select Uncertain, not Exact.";
    if (matchQuality !== "EXACT" && !matchNotes.trim()) return "Explain uncertain or partial matching.";
    if (status === "COMPLETE" && (count === null || !filtersVerified || !appliedFilters.trim() ||
      !sourceNotes.trim() || matchQuality !== "EXACT")) {
      return "Complete requires observed count, verified filters, source notes and an exact comparable.";
    }
    if (["NEEDS_REVIEW", "INSUFFICIENT_EVIDENCE"].includes(status) && !sourceNotes.trim()) {
      return "Explain the review/insufficient-evidence reason in source notes.";
    }
    return "";
  };

  const save = async () => {
    const issue = validate();
    if (issue) { setError(issue); setNotice(""); return; }
    setSaving(true); setError(""); setNotice("");
    const amount = (val) => val === "" ? null : Number(val);
    const payload = {
      listing_id: Number(run.listing_id), research_source: SOURCE,
      marketplace: "EBAY-AU", tab_name: "SOLD", period_days: 30,
      period_start: periodStart, period_end: periodEnd, search_query: fullQuery,
      exclusion_terms: exclusions.trim(), research_url: researchUrl,
      applied_filters: appliedFilters.trim(), filters_verified: filtersVerified,
      sold_count: soldCount === "" ? null : Number(soldCount), average_price: amount(average),
      low_price: amount(low), high_price: amount(high), confirm_no_sales: confirmNoSales,
      price_basis: priceBasis, match_quality: matchQuality, match_notes: matchNotes.trim(),
      source_notes: sourceNotes.trim(), observed_at: observedAt,
      evidence_status: status,
    };
    try {
      const { data } = await axios.post(`/api/ai-operator/research-runs/${run.id}/ebay-product-research`, payload);
      if (data?.ok !== true || !data.research?.latest || data.research.listing_id !== run.listing_id) {
        throw new Error("The backend did not verify saving Product Research evidence");
      }
      setHistory(data.research.revisions || []);
      setNotice(`Evidence revision #${data.research.latest.revision_no} saved (${status.replaceAll("_", " ")}).`);
      await onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to save Product Research");
    } finally { setSaving(false); }
  };

  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
    <DialogTitle>AI Operator — eBay Product Research · Listing #{run?.listing_id}</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip label={`Run #${run?.id}`} size="small" /><Chip label={`SKU ${display(snapshot.sku)}`} size="small" />
          <Chip label={`Finish: ${verifiedFinish(snapshot.fc256_verified_finish) || "Unknown / needs verification"}`} size="small" color={hasFinish ? "default" : "warning"} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{display(snapshot.card_name)} — {display(snapshot.listing_title)}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Set: {display(snapshot.card_set)} · Stored price: {display(snapshot.listing_price)} AUD · Stored qty: {display(snapshot.ebay_available_quantity)} · eBay listing ID: {display(snapshot.ebay_listing_id)}. Identity is from the immutable FC261 snapshot, not live eBay.
        </Typography>
        <Alert severity="info">Guided research only. Use eBay AU Seller Hub Product Research, visually verify SOLD, 30-day range and applied filters, and exclude other prints/finishes or mixed lots. The URL alone does not prove the filters or evidence.</Alert>
        {!hasFinish && <Alert severity="warning">Verified finish is Unknown. Do not mark comparable identity Exact or the result Complete until the identity is verified in the source listing.</Alert>}
        {readOnly && <Alert severity="info">This research run is closed. Evidence is read-only.</Alert>}
        {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography>Loading saved research…</Typography></Stack>}
        {error && <Alert severity="error">{error}</Alert>}
        {notice && <Alert severity="success">{notice}</Alert>}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Guided eBay SOLD search</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField label="Card name + collector number (editable)" fullWidth value={baseQuery} disabled={loading || readOnly || saving}
            onChange={(e) => setBaseQuery(e.target.value)}
            helperText="Broad SOLD search: set, finish, condition and rarity are not automatically added. Check comparable results manually." />
          <TextField label="Exclusions (comma-separated)" fullWidth value={exclusions} disabled={loading || readOnly || saving}
            placeholder="proxy, jumbo, lot" onChange={(e) => setExclusions(e.target.value)} />
        </Stack>
        <TextField label="Exact generated search query" value={fullQuery} fullWidth InputProps={{ readOnly: true }} />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="contained" startIcon={<OpenInNewIcon />} disabled={!fullQuery || loading}
            onClick={() => { const w = window.open(researchUrl, "ebay-product-research"); w?.focus?.(); }}>
            Open eBay Product Research
          </Button>
          <Button variant="outlined" startIcon={<ContentCopyIcon />} disabled={!fullQuery} onClick={async () => {
            try { await navigator.clipboard.writeText(fullQuery); setCopyNotice("Search query copied."); }
            catch (_) { setCopyNotice("Copy failed. Select the search terms manually."); }
          }}>Copy Search Query</Button>
          <Chip label="EBAY-AU · SOLD · 30-day launch" color="primary" variant="outlined" />
          {copyNotice && <Typography variant="caption" sx={{ alignSelf: "center" }}>{copyNotice}</Typography>}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>Generated launch URL: {researchUrl}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField label="Observed period start" type="date" InputLabelProps={{ shrink: true }} fullWidth value={periodStart}
            disabled={readOnly || loading || saving} onChange={(e) => setPeriodStart(e.target.value)} />
          <TextField label="Observed period end" type="date" InputLabelProps={{ shrink: true }} fullWidth value={periodEnd}
            disabled={readOnly || loading || saving} onChange={(e) => setPeriodEnd(e.target.value)} />
        </Stack>
        <TextField multiline minRows={2} fullWidth label="Actual applied filters / deviations (as observed)" value={appliedFilters}
          disabled={readOnly || loading || saving} onChange={(e) => setAppliedFilters(e.target.value)}
          helperText="Record actual filters; do not copy planned filters without checking Seller Hub." />
        <FormControlLabel control={<Checkbox checked={filtersVerified} disabled={readOnly || loading || saving}
          onChange={(e) => setFiltersVerified(e.target.checked)} />} label="I visually verified the SOLD tab, marketplace, period and applied filters in eBay Product Research" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Observed sold data (AUD)</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" }, gap: 2 }}>
          <TextField label="Sold count (blank = unknown)" type="number" value={soldCount} disabled={readOnly || loading || saving}
            onChange={(e) => { setSoldCount(e.target.value); setConfirmNoSales(false); }} />
          <TextField label="Average sold price" value={average} disabled={readOnly || loading || saving} onChange={(e) => setAverage(e.target.value)} />
          <TextField label="Lowest sold price (optional)" value={low} disabled={readOnly || loading || saving} onChange={(e) => setLow(e.target.value)} />
          <TextField label="Highest sold price (optional)" value={high} disabled={readOnly || loading || saving} onChange={(e) => setHigh(e.target.value)} />
        </Box>
        <FormControlLabel control={<Checkbox checked={confirmNoSales} disabled={readOnly || loading || saving || soldCount !== "0"}
          onChange={(e) => setConfirmNoSales(e.target.checked)} />} label="Confirm Product Research genuinely showed ZERO matching sales (not missing/unavailable results)" />
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField select fullWidth label="Observed price basis" value={priceBasis} disabled={readOnly || loading || saving}
            onChange={(e) => setPriceBasis(e.target.value)}>
            <MenuItem value="UNKNOWN">Unknown</MenuItem><MenuItem value="EXCLUDES_SHIPPING">Excludes shipping</MenuItem>
            <MenuItem value="INCLUDES_SHIPPING">Includes shipping</MenuItem>
          </TextField>
          <TextField select fullWidth label="Comparable match quality" value={matchQuality} disabled={readOnly || loading || saving}
            onChange={(e) => setMatchQuality(e.target.value)}>
            <MenuItem value="UNCERTAIN">Uncertain</MenuItem><MenuItem value="PARTIAL">Partial</MenuItem>
            {hasFinish && <MenuItem value="EXACT">Exact (verified print and finish)</MenuItem>}
          </TextField>
          <TextField select fullWidth label="Evidence state" value={status} disabled={readOnly || loading || saving}
            onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="IN_PROGRESS">In Progress</MenuItem><MenuItem value="NEEDS_REVIEW">Needs Review</MenuItem>
            <MenuItem value="INSUFFICIENT_EVIDENCE">Insufficient Evidence</MenuItem><MenuItem value="COMPLETE">Complete (only verified exact results)</MenuItem>
          </TextField>
        </Stack>
        <TextField fullWidth multiline minRows={2} label="Comparable match notes / exclusions" value={matchNotes}
          disabled={readOnly || loading || saving} onChange={(e) => setMatchNotes(e.target.value)} />
        <TextField fullWidth multiline minRows={3} label="Source observations, evidence and uncertainty" value={sourceNotes}
          disabled={readOnly || loading || saving} onChange={(e) => setSourceNotes(e.target.value)} />
        <TextField fullWidth label="Observed at (ISO timestamp, timezone included)" value={observedAt}
          disabled={readOnly || loading || saving} onChange={(e) => setObservedAt(e.target.value)} />
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Evidence revisions — append-only audit</Typography>
          {history.length === 0 ? <Typography variant="body2" color="text.secondary">No saved Product Research evidence for this run.</Typography>
            : <Stack spacing={0.5}>{history.map((revision) => <Typography key={revision.id} variant="body2">
              #{revision.revision_no} — {revision.evidence_status.replaceAll("_", " ")} — {display(revision.created_at)} — Sold: {display(revision.evidence_json?.sold_count, "Unknown")} — Avg: {display(revision.evidence_json?.average_price, "Unknown")} AUD
            </Typography>)}</Stack>}
        </Box>
      </Stack>
    </DialogContent>
    <DialogActions sx={{ flexWrap: "wrap", px: 3, py: 2 }}>
      <Button onClick={onClose} disabled={saving}>Close</Button>
      {!readOnly && <Button onClick={save} variant="contained" disabled={loading || saving}>{saving ? "Saving…" : history.length ? "Save New Research Revision" : "Save Research"}</Button>}
    </DialogActions>
  </Dialog>;
}
